import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import QuantitySelector from 'components/commons/QuantitySelector';
import BestPriceDisplay from 'components/commons/BestPriceDisplay';
import { productPropTypes } from 'helpers/propTypes';
import { formatCurrencyARS } from 'helpers/currency';
import './ItemD5.scss';

const ItemD5 = ({
  colors = [],
  combinations = {},
  combinationsTalle = {},
  className = '',
  item,
  onItemClick,
  onBuyClick,
  onQuantityChange,
  quantity,
  talles = [],
  getValidPriceRange,
  show,
  client,
  isTextil = false,
  cantMedidasMax = 2,
  renderInstallmentsBadge,
  paymentMethods,
  autoDiscounts,
}) => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [tallesXColor, setTallesXColor] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const unitPrice = item.sale > 0 ? item.sale : item.price;

  const selectedColorImage = selectedColor
    ? (selectedColor.image_2 || selectedColor.image_1)
    : item.image1;
  const imageSource = selectedColor ? (selectedColorImage || item.image1) : item.image1;
  const hoverImageSource = selectedColor
    ? (selectedColor.image_1 && selectedColor.image_2 ? selectedColor.image_1 : (item.image2 || null))
    : (item.image2 || null);

  const hasNonNullPrice = talles.some(t => t.price !== null && t?.price > 0);
  const hasSale = Number(item.sale) > 0;
  const currentPrice = hasSale ? item.sale : item.price;
  const oldPrice = hasSale ? item.price : null;
  const discountPct = hasSale ? Math.round(100 - (item.sale / item.price) * 100) : null;

  const hasCombos = Array.isArray(item.product_wholesaler) && item.product_wholesaler.length > 0;
  const hasDescriptions = Array.isArray(item.stock_product_descriptions) && item.stock_product_descriptions.length > 0;
  const canBuyDirect = !hasCombos && !hasDescriptions && item.stock > 0;

  const colorsReducidos = colors.reduce((acc, obj) => {
    const exists = acc.some(({ stock_description_id }) => stock_description_id === obj.stock_description_id);
    if (!exists) acc.push(obj);
    return acc;
  }, []);

  const handleColorSelect = (color, descriptions) => {
    setSelectedColor(color);
    setTallesXColor(descriptions);
  };

  const handleMouseEnter = () => {
    let i = -1;
    const id = setInterval(() => {
      if (i < colorsReducidos.length && i !== -1) {
        setSelectedColor(colorsReducidos[i]);
        i += 1;
      } else {
        setSelectedColor(null);
        i = 0;
      }
    }, 1500);
    setIntervalId(id);
  };
  const handleMouseLeave = () => {
    clearInterval(intervalId);
    setIntervalId(null);
    setSelectedColor(null);
  };

  const dots = (w) => (w?.length > 60 ? `${w.substring(0, 58)}…` : w);

  // Overlay sólo si NO es compra directa
  const getOverlayNode = () => {
    if (hasCombos) {
      return (
        <button type="button" className="itd5-btn" onClick={onItemClick}>
          Ver Combos
        </button>
      );
    }
    if (hasDescriptions) {
      return null;
    }
    if (!canBuyDirect && item.stock <= 0) {
      return <span className="itd5-out">Sin stock</span>;
    }
    return null; // canBuyDirect => sin overlay (lo mostramos inline)
  };

  const renderBlankMeasureSpace = () => {
    const hasColors = colorsReducidos.length > 0;
    const tallesCount = tallesXColor?.length ?? (talles?.length ?? 0);
    const hasTalles = tallesCount > 0;
    const onlyColors = hasColors && !hasTalles;

    const baseRows = !onlyColors ? (Number(cantMedidasMax) || 0) + 1 : (Number(cantMedidasMax) || 0) + 1;
    const adjustedRows = Math.max(0, baseRows - (hasColors ? 1 : 0) - (hasTalles ? 1 : 0));
    if (adjustedRows === 0) return null;

    return (
      <div className="pt-price-table">
        {Array.from({ length: adjustedRows }).map((_, i) => (
          <div key={i} className={`pt-price-row ${onlyColors ? 'row-onlycolors' : ''}`}>
            <span className="pt-price-label">&nbsp;</span>
            <span className="pt-price-value"></span>
          </div>
        ))}
      </div>
    );
  };

  const categoryName = item?.categories?.[0]?.name;
  const overlayNode = getOverlayNode();

  const handleBuy = (e) => {
    e.stopPropagation();
    (typeof onBuyClick === 'function' ? onBuyClick : onItemClick)();
  };

  return (
    <article className={`itd5-card ${className}`.trim()} data-testid={`product-${item.id}`}>
      {hasSale && <span className="itd5-badge">-{discountPct}%</span>}

      <div
        className="itd5-media"
        onClick={onItemClick}
        role="presentation"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img className={`itd5-img${hoverImageSource ? ' itd5-img--primary' : ''}`} src={imageSource} alt={item.name} />
        {!!hoverImageSource && <img className="itd5-img itd5-img--hover" src={hoverImageSource} alt="" aria-hidden="true" />}
        {overlayNode && <div className="itd5-overlay">{overlayNode}</div>}
      </div>

      <div className="itd5-body">
        {categoryName && <div className="itd5-cat">{categoryName}</div>}

        <h3 className="itd5-title" onClick={onItemClick} role="presentation">
          {dots(item.name)}
        </h3>

        <div className="product-badges">
          {renderInstallmentsBadge()}
        </div>

        {/* swatches de color */}
        {colorsReducidos.length > 0 && (
          <div className="itd5-swatches">
            {colorsReducidos.slice(0, 5).map((c) => {
              const isMulti = c?.stock_descriptions?.name === 'Multicolor';
              const bg = isMulti ? {} : { backgroundColor: c?.stock_descriptions?.rgb };
              const active = selectedColor?.stock_description_id === c.stock_description_id;
              return (
                <span
                  key={`sw-${c.stock_description_id}`}
                  className={`itd5-swatch${active ? ' active' : ''}${isMulti ? ' multi' : ''}`}
                  style={bg}
                  role="presentation"
                  onClick={() => handleColorSelect(c, combinations[c.stock_description_id])}
                />
              );
            })}
            {colorsReducidos.length > 5 && <span className="itd5-more">+{colorsReducidos.length - 5}</span>}
          </div>
        )}

        {/* talles */}
        <div className="itd5-sizes">
          {talles.length > 0 && (
            <>
              {!tallesXColor
                ? Object.keys(combinationsTalle || {}).map((id) => {
                    const talle = talles.find(
                      ({ stock_description_id }) => stock_description_id === Number(id)
                    );
                    if (!talle) return null;
                    return (
                      <span key={`t-${talle.stock_description_id}`} className="itd5-size">
                        {talle.stock_descriptions?.name}
                      </span>
                    );
                  })
                : tallesXColor.map((talle) => (
                    <span key={`t-${talle.stock_description_id}`} className="itd5-size">
                      {talle.stock_descriptions?.name}
                    </span>
                  ))}
            </>
          )}
        </div>

        {Number(cantMedidasMax) > 0 && renderBlankMeasureSpace()}

        {/* ⬇️ INLINE QTY ENTRE NOMBRE Y PRECIO – SOLO compra directa */}
        {canBuyDirect && (
          <div className="itd5-inline-qty">
            <QuantitySelector
              limit={item.stock}
              onQuantityChange={onQuantityChange}
              quantity={quantity}
              show={show}
            />
          </div>
        )}

        {/* precios */}
        <div className="itd5-prices">
          {!hasNonNullPrice ? (
            <>
              <span className="itd5-price">{formatCurrencyARS(currentPrice)}</span>
              {oldPrice ? <span className="itd5-old">{formatCurrencyARS(oldPrice)}</span> : null}
            </>
          ) : (
            <span className="itd5-price itd5-range">{getValidPriceRange()}</span>
          )}
        </div>

        <BestPriceDisplay 
          unitPrice={unitPrice} 
          autoDiscounts={autoDiscounts} 
          paymentMethods={paymentMethods} 
          show={show}
          isItem={true}
        />

        <button type="button" className="api-buy-btn" onClick={handleBuy}>
          Comprar
        </button>
      </div>
    </article>
  );
};

ItemD5.defaultProps = {
  combinations: {},
  combinationsTalle: {},
  className: '',
};

ItemD5.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  combinations: PropTypes.shape(),
  combinationsTalle: PropTypes.shape(),
  className: PropTypes.string,
  item: productPropTypes.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onBuyClick: PropTypes.func,
  onQuantityChange: PropTypes.func.isRequired,
  quantity: PropTypes.number.isRequired,
  talles: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  getValidPriceRange: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  client: PropTypes.shape(),
  isTextil: PropTypes.bool,
  cantMedidasMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ItemD5;
